import React from 'react';
import { motion } from 'framer-motion';
import { FiFileText, FiMail } from 'react-icons/fi';

const TermsOfService = () => {
  const sections = [
    {
      id: 1,
      title: "Acceptance of Terms",
      content: (
        <>
          <p className="text-gray-400 mb-3">1.1 By using the Service, you confirm that you have read, understood, and agree to be legally bound by these Terms.</p>
          <p className="text-gray-400">1.2 The Singapore Parent and Panama Operations Entity reserve the right to refuse service to any user for any reason, in accordance with applicable law.</p>
        </>
      )
    },
    {
      id: 2,
      title: "Description of Service",
      content: (
        <>
          <p className="text-gray-400 mb-3">2.1 The Service includes AI-powered chart analysis, pattern recognition, discipline-based insights, and related tools for evaluating financial markets.</p>
          <p className="text-gray-400">2.2 The Service is provided for informational and educational purposes only. Neither entity guarantees accuracy or suitability for investment decisions.</p>
        </>
      )
    },
    {
      id: 3,
      title: "No Financial Advice",
      content: (
        <>
          <p className="text-gray-400 mb-3">3.1 The Service does not constitute financial, investment, trading, tax, or legal advice.</p>
          <p className="text-gray-400">3.2 Users remain solely responsible for all trading decisions. Neither the Singapore Parent nor the Panama Operations Entity shall be liable for any trading gains or losses.</p>
        </>
      )
    },
    {
      id: 4,
      title: "Risk Disclosure",
      content: (
        <>
          <p className="text-gray-400 mb-3">4.1 Trading financial instruments, including derivatives and cryptocurrencies, carries significant risk. Losses may exceed invested capital.</p>
          <p className="text-gray-400">4.2 Users should carefully evaluate their financial situation and risk tolerance before using the Service.</p>
        </>
      )
    },
    {
      id: 5,
      title: "Subscriptions and Payments",
      content: (
        <>
          <p className="text-gray-400 mb-3">5.1 Some features require paid subscriptions. By subscribing, you agree to pay all applicable fees.</p>
          <p className="text-gray-400">5.2 Subscriptions renew automatically unless canceled before renewal. Refunds are issued at the discretion of the Singapore Parent.</p>
        </>
      )
    },
    {
      id: 6,
      title: "Data Privacy",
      content: (
        <>
          <p className="text-gray-400 mb-3">6.1 Personal data is processed in accordance with our Privacy Policy.</p>
          <p className="text-gray-400">6.2 Users consent to data collection and usage for service improvement, AI optimization, fraud prevention, and compliance.</p>
        </>
      )
    },
    {
      id: 7,
      title: "User Responsibilities",
      content: (
        <>
          <p className="text-gray-400 mb-3">7.1 Users must protect their account credentials and are responsible for all activities under their accounts.</p>
          <p className="text-gray-400">7.2 Users agree not to use the Service unlawfully, bypass security, reverse-engineer components, or share credentials.</p>
        </>
      )
    },
    {
      id: 8,
      title: "Intellectual Property",
      content: (
        <>
          <p className="text-gray-400 mb-3">8.1 All intellectual property, including software, algorithms, code, AI models, architecture, design, and trademarks, is the exclusive property of Quantum AI FinTech Pte. Ltd. (Singapore Parent).</p>
          <p className="text-gray-400 mb-3">8.2 The Panama Operations Entity is authorized only as an administrator and operator.</p>
          <p className="text-gray-400">8.3 Users may not copy, distribute, reproduce, or create derivative works without written authorization from the Singapore Parent.</p>
        </>
      )
    },
    {
      id: 9,
      title: "Limitation of Liability",
      content: (
        <>
          <p className="text-gray-400 mb-3">9.1 To the fullest extent permitted by law, neither entity shall be liable for any direct or indirect damages, including loss of profits, trading losses, or interruption of service.</p>
          <p className="text-cyan-400 text-sm">9.2 The Service is provided "as-is" and "as available" without warranties of any kind.</p>
        </>
      )
    },
    {
      id: 10,
      title: "Indemnification",
      content: (
        <>
          <p className="text-gray-400 mb-4">Users agree to indemnify and hold harmless the Singapore Parent, the Panama Operations Entity, and their officers, directors, and employees from any claims arising from:</p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4">
            <li>(a) use of the Service</li>
            <li>(b) violation of these Terms</li>
            <li>(c) violation of applicable laws</li>
          </ul>
        </>
      )
    },
    {
      id: 11,
      title: "Modifications",
      content: (
        <p className="text-gray-400">The Terms may be updated at any time. Continued use of the Service constitutes acceptance of updated Terms.</p>
      )
    },
    {
      id: 12,
      title: "Termination",
      content: (
        <p className="text-gray-400">Access may be suspended or terminated for violations of these Terms or other legitimate reasons.</p>
      )
    },
    {
      id: 13,
      title: "Governing Law",
      content: (
        <>
          <p className="text-gray-400 mb-3">13.1 Intellectual property, ownership, and corporate governance are governed by Singapore law.</p>
          <p className="text-gray-400 mb-3">13.2 Operational and administrative matters may be governed by Panama law.</p>
          <p className="text-gray-400">13.3 Disputes will be resolved in the jurisdiction appropriate to the nature of the dispute.</p>
        </>
      )
    },
    {
      id: 14,
      title: "Severability",
      content: (
        <p className="text-gray-400">If any provision is found invalid or unenforceable, the remaining provisions remain in effect.</p>
      )
    },
    {
      id: 15,
      title: "Entire Agreement",
      content: (
        <p className="text-gray-400">These Terms, together with the Privacy Policy and subscription agreements, represent the complete agreement between the user and the Singapore Parent.</p>
      )
    },
    {
      id: 16,
      title: "Contact Information",
      content: (
        <>
          <p className="text-gray-400 mb-4">For support or questions, contact:</p>
          <div className="p-4 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/20 rounded-xl inline-block">
            <a href="mailto:support@qfintech.ai" className="text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-2 font-medium">
              <FiMail className="text-lg" />
              support@qfintech.ai
            </a>
          </div>
        </>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-cyan-600/10"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-r from-purple-600/20 to-cyan-600/20 blur-3xl rounded-full"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
                <FiFileText className="text-3xl text-cyan-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-black mb-4">
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                TERMS OF SERVICE
              </span>
            </h1>
            <p className="text-gray-400 text-sm">
              Effective Date: December 6, 2025
            </p>
          </motion.div>
        </div>
      </div>

      {/* Introduction */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="p-6 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/20 rounded-2xl"
        >
          <p className="text-gray-300 leading-relaxed mb-4">
            <span className="text-cyan-400 font-medium">SnapChart AI</span> and <span className="text-cyan-400 font-medium">TitanVue AI</span> (collectively, the "Service") are owned by <span className="text-cyan-400 font-medium">Quantum AI FinTech Pte. Ltd.</span>, a Singapore corporation ("Singapore Parent"), and operated administratively by <span className="text-cyan-400 font-medium">Quantum AI FinTech Corporation SA</span>, a Panama company ("Panama Operations Entity"). The Service provides AI-powered financial charting and analytical tools.
          </p>
          <p className="text-gray-400">
            By accessing or using the Service, you agree to be bound by these Terms and Conditions (the "Terms"). If you do not agree, you must stop using the Service immediately.
          </p>
        </motion.div>
      </div>

      {/* Sections */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="space-y-8">
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="p-6 bg-gray-800/30 border border-white/5 rounded-2xl hover:border-purple-500/20 transition-colors"
            >
              <h3 className="text-xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-cyan-400 text-sm font-bold">
                  {section.id}
                </span>
                <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  {section.title}
                </span>
              </h3>
              <div className="pl-11">
                {section.content}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
